// Cycle 23 B3 C5 (Madde 3) — product-hero orphaned-picker fix.
//
// The bespoke `product-hero` section renders a standalone <variant-selects>
// with NO <product-info> wrapper, so Dawn's option→variant→hidden-id machine
// (product-info.js) never subscribes and the hidden #product-form-hero
// [name="id"] input stays on the first server-rendered variant — the cart
// always received variant 1 regardless of the shopper's selection.
//
// This script retrofits ONLY the missing link, section-scoped:
//   1. read the embedded variant map (`#phero-variant-map-<sectionId>`),
//   2. subscribe to PUB_SUB_EVENTS.optionValueSelectionChange (native
//      VariantSelects already publishes it on every option change),
//   3. read the selected option value strings from <variant-selects>,
//   4. find the matching variant, write the hidden [name="id"], update the
//      .phero__price / .phero__compare, toggle availability, scroll the
//      gallery to the variant's featured image when it has one (Cycle 23 V),
//   5. publish PUB_SUB_EVENTS.variantChange — bundle-selector.js already
//      listens and re-syncs its config + hidden id + bundle price for free.
//
// It does NOT retrofit Dawn's #price-{id}/#Sku-{id} conventions (this section
// uses bespoke `.phero__*` classes). Single-variant products render no picker,
// so this whole file is a no-op there.

(function () {
  'use strict';

  function readMap(sectionId) {
    var el = document.getElementById('phero-variant-map-' + sectionId);
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // Selected option VALUE strings, in option order (matches variant.options).
  // Works for both button/radio pickers (`fieldset input:checked`) and the
  // dropdown picker (`select option[selected]`) — the same selector Dawn's
  // VariantSelects.selectedOptionValues uses, mapped to `.value` not the id.
  function selectedValues(variantSelects) {
    return Array.prototype.slice
      .call(variantSelects.querySelectorAll('select option[selected], fieldset input:checked'))
      .map(function (node) {
        return node.value;
      });
  }

  function matchVariant(variants, values) {
    return (
      variants.find(function (v) {
        return (
          Array.isArray(v.options) &&
          v.options.length === values.length &&
          v.options.every(function (opt, i) {
            return opt === values[i];
          })
        );
      }) || null
    );
  }

  // Cycle 23 L (Madde 13) — standard buy-button contract. Third-party
  // buy-button apps (COD forms etc.) can only see a variant selection through
  // the standard theme signals: a 'change' event on the cart form's hidden
  // [name=id] input and the ?variant= URL param (many apps monkey-patch
  // history.replaceState and re-read it on every write — the most robust
  // channel). A bare `.value =` write is invisible to both. This helper is the
  // single write-path for the hidden id; bundle-selector.js carries its own
  // copy (the two assets load independently).
  function syncFormSignals(variantId) {
    if (!variantId) return;
    var form = document.getElementById('product-form-hero');
    var idInput = form ? form.querySelector('[name="id"]') : null;
    if (idInput) {
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    try {
      var params = new URLSearchParams(window.location.search);
      params.set('variant', String(variantId));
      window.history.replaceState(
        {},
        '',
        window.location.pathname + '?' + params.toString() + window.location.hash
      );
    } catch (e) {
      // URL sync is best-effort — never break the PDP over it.
    }
  }

  // Currency-agnostic money formatter, mirroring bundle-selector.js: prefer
  // window.Shopify.currency.active, fall back to the storefront-i18n bridge,
  // then a bare amount. Input is Shopify cents.
  function formatMoney(cents) {
    var i18n = {};
    try {
      var node = document.getElementById('ecomnodes-storefront-i18n');
      if (node) i18n = JSON.parse(node.textContent || '{}') || {};
    } catch (e) {
      i18n = {};
    }
    var shop = (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency) || null;
    var currencyCode = (shop && shop.active) || i18n.currencyCode || 'USD';
    var locale = i18n.intlLocale || 'en-US';
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currencyCode;
    }
  }

  function initSection(variantSelects) {
    var sectionId = variantSelects.dataset.section;
    if (!sectionId) return;
    var variants = readMap(sectionId);
    if (!variants) return;

    var priceEl = document.querySelector('.phero__price');
    var compareEl = document.querySelector('.phero__compare');

    function warnEl() {
      var el = document.querySelector('.phero__variant-warning');
      if (!el) {
        el = document.createElement('div');
        el.className = 'phero__variant-warning';
        el.setAttribute('role', 'alert');
        el.setAttribute('aria-live', 'polite');
        el.style.display = 'none';
        variantSelects.insertAdjacentElement('afterend', el);
      }
      return el;
    }

    function apply(variant) {
      if (!variant) return;
      // Hidden id + change dispatch + ?variant= URL — MUST land before the
      // variantChange publish below so bundle-selector re-seeds its unit
      // pickers from the fresh id.
      syncFormSignals(variant.id);

      // Price mirror. When bundle-selector.js is present its variantChange
      // handler re-renders the (bundle-adjusted) price synchronously right
      // after this, overriding these writes — so this only surfaces for a hero
      // without a bundle selector. compare_at only shown when strictly higher.
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      // Availability — re-query the button each time (bundle-selector clones it).
      var cartBtn = document.querySelector('.phero__cart-btn');
      var warning = warnEl();
      if (variant.available === false) {
        if (cartBtn) cartBtn.setAttribute('disabled', 'disabled');
        warning.textContent = 'Seçtiğiniz varyant şu an stokta yok. Lütfen başka bir seçenek deneyin.';
        warning.style.display = '';
      } else {
        if (cartBtn) cartBtn.removeAttribute('disabled');
        warning.style.display = 'none';
      }

      // Cycle 23 V — scroll the gallery to the variant's featured image (only
      // when the variant has one). Runs before the publish; neither depends
      // on the other.
      swapGalleryImage(variant);

      // Hand off to bundle-selector (hidden id + config + bundle price sync).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { variant: { id: variant.id, price: variant.price, available: variant.available } },
        });
      }
    }

    // Cycle 23 V — variant image swap. Map rows carry `image_index`: the
    // 1-based position of the variant's featured image inside product.images
    // (null when the variant has no image). The gallery main wrap is a
    // horizontal snap carousel rendering product.images in the same order, so
    // slide N sits at (N-1) × offsetWidth — the exact math the thumbnail
    // onclick uses. A variant without an image leaves the gallery untouched;
    // a missing wrap / thumbs (single-image product) is silently skipped.
    function swapGalleryImage(variant) {
      var idx = variant.image_index;
      if (typeof idx !== 'number' || idx < 1) return;
      var wrap = document.getElementById('PHeroMainWrap-' + sectionId);
      if (!wrap || typeof wrap.scrollTo !== 'function') return;
      wrap.scrollTo({ left: (idx - 1) * wrap.offsetWidth, behavior: 'smooth' });
      var thumbs = document.querySelectorAll('.phero__thumb');
      Array.prototype.forEach.call(thumbs, function (thumb) {
        thumb.classList.toggle('is-active', Number(thumb.dataset.index) === idx - 1);
      });
    }

    function onChange() {
      apply(matchVariant(variants, selectedValues(variantSelects)));
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, onChange);
    } else {
      // pubsub unavailable — fall back to a direct listener so selection still
      // drives the hidden id (bundle-selector's variantChange bridge is then
      // skipped, but the cart still gets the right variant).
      variantSelects.addEventListener('change', onChange);
    }
  }

  function init() {
    var pickers = document.querySelectorAll('variant-selects[data-section]');
    Array.prototype.forEach.call(pickers, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
